// ignore-string-externalization
import type { ErrorEvent, SeverityLevel } from '@sentry/types';

export type KnownIssue = {
  id: string;
  description: string;
  resolvedBy: string;
  match: (event: ErrorEvent) => boolean;
  level: SeverityLevel;
};

export const knownIssues: KnownIssue[] = [
  {
    id: 'onetrust-sdk-domain',
    description:
      'OneTrust consent SDK throws TypeError on t.Domain when the GPP ' +
      'privacy framework section is parsed. denyUrls blocks most of these, ' +
      'but the Sentry instrument mechanism (XMLHttpRequest wrapping) causes ' +
      'some to bypass the URL check.',
    resolvedBy:
      'OneTrust fixes the GPP parsing bug in their SDK. This entry is a ' +
      'backstop for denyUrls — remove if OneTrust stops throwing.',
    level: 'warning',
    match(event) {
      const ex = event.exception?.values?.[0];
      const msg = ex?.value ?? '';
      const frames = ex?.stacktrace?.frames ?? [];
      return (
        /is not an object \(evaluating '.*\.Domain'\)/.test(msg) &&
        frames.some(f => /otSDKStub/.test(f.filename ?? ''))
      );
    },
  },
  {
    id: 'stylis-stack-overflow',
    description:
      'styled-components stylis CSS serialization overflows the call stack on iOS devices ' +
      'with limited stack depth. Root cause is recursive AST traversal in stylis ' +
      'serialize/stringify — not an infinite loop, just too deep for iOS WebKit.',
    resolvedBy:
      'Upgrade to styled-components v7 (replaces stylis with a non-recursive parser). ' +
      'Remove this entry and the @sentry/types dev dependency when resolved.',
    level: 'warning',
    // Frame-level analysis is not possible: the Sentry SDK drops stack
    // lines longer than 1024 chars (STACKTRACE_FRAME_LIMIT in
    // @sentry/utils/stacktrace.js), and every frame in these minified
    // bundles exceeds that. The frames array is empty in beforeSend.
    match(event) {
      const ex = event.exception?.values?.[0];
      return /Maximum call stack size exceeded/.test(ex?.value ?? '');
    },
  },
  {
    id: 'third-party-injected-script',
    description:
      'Errors from browser extensions and injected scripts that reference ' +
      'variables not present in our code (swbrowser, GM_*, ethereum, etc.). ' +
      'Stack traces point to anonymous eval contexts, not our bundles.',
    resolvedBy:
      'Cannot be fixed — third-party code. This entry prevents PagerDuty alerts. ' +
      'Remove only if Sentry adds native extension filtering.',
    level: 'warning',
    // Only match truly anonymous frames (eval, inline, no filename) — not
    // errors from third-party CDN scripts (Mux, Contentful) which could
    // be real bugs we'd want to know about.
    match(event) {
      const ex = event.exception?.values?.[0];
      const msg = ex?.value ?? '';
      const frames = ex?.stacktrace?.frames ?? [];
      const allAnonymous =
        frames.length > 0 &&
        frames.every(
          f =>
            !f.filename ||
            f.filename === '<anonymous>' ||
            f.filename.startsWith('[native'),
        );
      if (!allAnonymous) return false;
      return (
        /is not defined/.test(msg) ||
        /is not an object/.test(msg) ||
        /Cannot read propert/.test(msg)
      );
    },
  },
];
